import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ChefHat, Clock, Flame, Users } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useRecipe } from '../hooks/useRecipes';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { richTextHtml } from '../lib/richText';
import { minutesLabel } from '../types/recipe';
import { Product, PRODUCT_SELECT } from '../types/product';
import { productImage } from '../lib/productImage';
import { priceLabel } from '../lib/variants';
import NotFound from './NotFound';

/** Pagina di una ricetta pubblicata (/ricette/<slug>). */
export default function RecipeDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { recipe, loading, notFound } = useRecipe(slug);
  const [product, setProduct] = useState<Product | null>(null);

  useDocumentMeta(recipe?.title, recipe?.summary || undefined);

  // Il miele consigliato, solo se ancora in vendita.
  useEffect(() => {
    if (!recipe?.product_id) {
      setProduct(null);
      return;
    }
    supabase
      .from('products')
      .select(PRODUCT_SELECT)
      .eq('id', recipe.product_id)
      .eq('active', true)
      .maybeSingle()
      .then(({ data }) => setProduct((data as Product | null) ?? null));
  }, [recipe?.product_id]);

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-t-2 border-primary" />
      </div>
    );
  }
  // Anche una bozza aperta dal link diretto: per i visitatori non esiste.
  if (notFound || !recipe || !recipe.published) return <NotFound />;

  const facts = [
    recipe.servings && { icon: Users, label: 'Porzioni', value: String(recipe.servings) },
    recipe.prep_minutes && { icon: Clock, label: 'Preparazione', value: minutesLabel(recipe.prep_minutes) },
    recipe.cook_minutes && { icon: Flame, label: 'Cottura', value: minutesLabel(recipe.cook_minutes) },
  ].filter(Boolean) as { icon: typeof Users; label: string; value: string }[];

  return (
    <article>
      <header className="relative overflow-hidden bg-[#2F1500]">
        {recipe.image_url && <img src={recipe.image_url} alt="" className="absolute inset-0 h-full w-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#2F1500]/90 via-[#2F1500]/60 to-[#2F1500]/30" />
        <div className="container relative z-10 py-16 md:py-24">
          <Link to="/ricette" className="mb-8 inline-flex items-center gap-1.5 text-sm font-semibold text-white/80 hover:text-white">
            <ArrowLeft className="h-4 w-4" />
            Tutte le ricette
          </Link>
          <h1 className="max-w-3xl font-display text-4xl font-bold leading-tight text-white text-balance md:text-5xl">{recipe.title}</h1>
          {recipe.summary && <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/90 md:text-lg">{recipe.summary}</p>}
          {facts.length > 0 && (
            <dl className="mt-8 flex flex-wrap gap-x-8 gap-y-3">
              {facts.map(({ icon: Icon, label, value }) => (
                <div key={label} className="flex items-center gap-2 text-white">
                  <Icon className="h-5 w-5 text-[#FFB95F]" />
                  <dt className="sr-only">{label}</dt>
                  <dd className="text-sm">
                    <span className="text-white/70">{label}: </span>
                    <span className="font-semibold">{value}</span>
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      </header>

      <div className="container grid gap-10 py-14 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] lg:gap-14">
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
            <h2 className="mb-4 font-display text-2xl font-semibold text-foreground">Ingredienti</h2>
            {recipe.ingredients.length > 0 ? (
              <ul className="divide-y divide-border">
                {recipe.ingredients.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 py-2.5 text-foreground/85">
                    <span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-primary" />
                    {item}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">Nessun ingrediente indicato.</p>
            )}
          </div>

          {product && (
            <Link
              to={`/products/${product.id}`}
              className="mt-6 flex items-center gap-4 rounded-2xl border border-border bg-card p-4 shadow-sm transition-shadow hover:shadow-md"
            >
              <img src={productImage(product.image_url)} alt="" className="h-16 w-16 flex-none rounded-xl object-cover" />
              <div className="min-w-0">
                <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Il miele per questa ricetta</p>
                <p className="font-display font-semibold text-foreground">{product.name}</p>
                <p className="ap-price text-sm font-bold text-primary">{priceLabel(product)}</p>
              </div>
            </Link>
          )}
        </aside>

        <section>
          <h2 className="mb-5 flex items-center gap-2 font-display text-2xl font-semibold text-foreground">
            <ChefHat className="h-6 w-6 text-primary" />
            Preparazione
          </h2>
          <div
            className="rich-text text-base leading-relaxed text-foreground/80 md:text-lg"
            dangerouslySetInnerHTML={{ __html: richTextHtml(recipe.description) }}
          />
        </section>
      </div>
    </article>
  );
}
